/**
 * The preprocessing steps that occur during a document's initial lifecycle.
 */
export type PreprocessingPhase =
  | 'VirusScanning'
  | 'CheckingForFileConversion'
  | 'ConvertingFile'
  | 'Finalizing'

/**
 * The reasons for which a document may require manual attestation.
 */
export type ManualAttestationReason =
  | 'ClientNameMismatch'
  | 'MissingProSignature'
  | 'ExtractionFaulted'
  | 'AttestationFaulted'
  | 'TimedOut'

/**
 * Reasons for which a document can be rejected by the system.
 */
export type DocumentRejection = 'Quarantined' | 'ConversionFailed'

/**
 * The document is undergoing initial processing.
 */
export interface PreprocessingStage {
  readonly type: 'Preprocessing'
  readonly phase: PreprocessingPhase
}

/**
 * The document is being screened to determine whether attestation is needed.
 */
export interface AttestationScreeningStage {
  readonly type: 'AttestationScreening'
}

/**
 * The document requires manual attestation before becoming available.
 */
export interface ManualAttestationRequiredStage {
  readonly type: 'ManualAttestationRequired'
  readonly reason?: ManualAttestationReason
}

/**
 * The document is fully processed and released.
 */
export interface ReleasedStage {
  readonly type: 'Released'
}

/**
 * The document was rejected by the system.
 */
export interface RejectedStage {
  readonly type: 'Rejected'
  readonly rejection: DocumentRejection
}

/**
 * The lifecycle stages a document travels through.
 */
export type DocumentStage =
  | PreprocessingStage
  | AttestationScreeningStage
  | ManualAttestationRequiredStage
  | ReleasedStage
  | RejectedStage

/**
 * @see {DocumentStage}
 */
export const DocumentStage = {
  /**
   * The stage a document starts its lifecycle in.
   */
  initial: {
    type: 'Preprocessing',
    phase: 'VirusScanning',
  } satisfies DocumentStage,
} as const
